import {Injectable} from '@angular/core';
import {getAge, Patient} from "../../model/patient";
import {Gender, ScanTypes} from "../../model/weak-entities";
import {Exam, ExamType, IsCorescore} from "../../model/exam";
import {Scan} from "../../model/scan";
import {ApiRestProvider} from "../api-rest/api-rest";
import {Doctor} from "../../model/doctor";
import {ScanViewUtils} from "../../components/scan-view/scan-view";
import {Practice} from "../../model/practice";

const MAX_PATIENT_HISTORY = 10;

@Injectable()
export class ExamProvider {
  public quickScanName: string;
  noExam: boolean = false;

  private _patient: Patient;
  public get patient(): Patient {
    if (this.currentExam && (this.currentExam.Doctor || this.currentExam.DoctorId)) {
      return this._patient;
    } else {
      return <Patient>{
        FirstName: 'Quick',
        LastName: 'Scan',
        Gender: Gender.Male
      }
    }
  };

  public doctor: Doctor;

  public examInProgress: boolean = false;

  get therm(): boolean {
    if(this.currentExam != null) {
      return !!this.currentExam.ThermalScanId;
    } else {
      return false;
    }
  }

  get emg(): boolean {
    if(this.currentExam != null) {
      return !!this.currentExam.EmgScanId;
    } else {
      return false;
    }
  }

  get hrv(): boolean {
    if(this.currentExam != null) {
      return !!this.currentExam.HrvScanId;
    } else {
      return false;
    }
  }

  get algo(): boolean {
    if(this.currentExam != null) {
      return !!this.currentExam.AlgoScanId;
    } else {
      return false;
    }
  }

  get rom(): boolean {
    if(this.currentExam != null) {
      return !!this.currentExam.RomScanId;
    } else {
      return false;
    }
  }

  get examId(): string {
    if(this.currentExam != null) {
      return this.currentExam.Id;
    } else {
      return null;
    }
  }
  public exams: Exam[];
  get examCount(): number {
    return this.exams.length;
  }
  public recentPts: HistoryItem[] = [];
  public getCurrentExam(): Exam {
    return this.currentExam;
  }
  private currentExam: Exam;
  constructor(public api: ApiRestProvider) {
  }

  public async logOut() {
    await this.clearPatient();
    this.doctor = null;
    this.recentPts.length = 0;
  }

  public async getDoctor() {
    this.doctor = await this.api.getDoctor().toPromise();
  }

  public async getExam() {
    this.exams = await this.api.getAllExamsForPatientWithId(this.currentExam.PatientId).toPromise();
    this.exams.sort((a,b) => a.CreatedStamp < b.CreatedStamp ? -1 : a.CreatedStamp > b.CreatedStamp ? 1: 0);
    let startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    let recentExams = this.exams.filter(e => {
      let scanDate = new Date(e.CreatedStamp);
      return scanDate > startDate;
    });

    if(recentExams != undefined && recentExams.length > 0)
    {
      this.currentExam = recentExams[0];
    }
  }

  public async getRecentExam(patient: Patient):Promise<ExamDetails> {
    let exams = await this.api.getAllExamsForPatientWithId(patient.Id).toPromise();
    exams.sort((a,b) => a.CreatedStamp < b.CreatedStamp ? -1 : a.CreatedStamp > b.CreatedStamp ? 1: 0);
    let startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    let recentExams = exams.filter(e => {
      let scanDate = new Date(e.CreatedStamp);
      return scanDate > startDate;
    });

    let details = <ExamDetails>{};
    if (recentExams != undefined && recentExams.length > 0)
    {
      details.examId = recentExams[0].Id;
      details.hrvId = recentExams[0].HrvScanId;
      details.emgId = recentExams[0].EmgScanId;
      details.thermalId = recentExams[0].ThermalScanId;
      details.algoId = recentExams[0].AlgoScanId;
      details.romId = recentExams[0].RomScanId;
    }
    return details;
  }

  // Sets a current patient to be examined.
  // This is persisted application wide
  public async setPatient(pt: Patient) {
    this.currentExam = null;
    this._patient = null;
    if(pt == null) return;

    this.currentExam = <Exam>{
      CoreScore: 0,
      HrvScore: 0,
      ThermalScore: 0,
      EmgScore: 0,
      AlgoScore: 0,
      RomScore: 0,
      DoctorId: this.doctor.Id,
      PatientId: pt.Id,
      CreatedStamp: new Date()
    };
    this._patient = pt;
    this.examInProgress = true;

    //Check if he is already in history
    this.recentPts = this.recentPts.filter(function (item) {
      return item.patient.Id != pt.Id;
    });

    // Add him to the history
    this.recentPts.push({
      patient: pt,
      timestamp: new Date()
    });
    // If the history is full
    while (this.recentPts.length > MAX_PATIENT_HISTORY) {
      // Remove old patients
      this.recentPts.shift();
    }

    // Update patient's visit stamp
    pt.VisitStamp = new Date();
    await this.api.modifyPatient(pt).toPromise();

    //this.getTodaysScans(pt);

    await this.getExam();
  }

  public async setScanScore(type: ScanTypes, score: number, version: string, exam: Exam = this.currentExam)
  {
    // NaN/Infinity serialize as null, which the API can't bind to a float and rejects the whole exam
    if (!isFinite(score)) {
      console.warn(`[Exam] non-finite ${ScanTypes[type]} score (${score}), saving 0`);
      score = 0;
    }

    switch (type) {
      case ScanTypes.EMG:
        exam.EmgScore = score;
        exam.EmgScoreVersion = version;
        break;
      case ScanTypes.Thermal:
        exam.ThermalScore = score;
        exam.ThermalScoreVersion = version;
        break;
      case ScanTypes.HRV:
        exam.HrvScore = score;
        exam.HrvScoreVersion = version;
        break;
      case ScanTypes.ROM:
        exam.RomScore = score;
        exam.RomScoreVersion = version;
        break;
      case ScanTypes.Algo:
        exam.AlgoScore = score;
        exam.AlgoScoreVersion = version;
        break;
    }
  }

  public async setScanScoreByScanId(type: ScanTypes, scanId: string) {
    const practice: Practice = await this.api.getPractice().toPromise();
    switch (type) {
      case ScanTypes.EMG:
        let emgScan = await ScanViewUtils.loadEmgScan(scanId, this.api);
        let emgBreakdown = await ScanViewUtils.getEmgBreakdown(emgScan);
        await this.setScanScore(ScanTypes.EMG, emgBreakdown.Score.toNumber(), emgBreakdown.ScoreVersion);
        break;
      case ScanTypes.Thermal:
        let thermScan = await ScanViewUtils.loadThermalScan(scanId, this.api);
        let thermBreakdown = await ScanViewUtils.getThermalBreakdown(thermScan, practice.DisableThermalShift);
        await this.setScanScore(ScanTypes.Thermal, thermBreakdown.Score.toNumber(), thermBreakdown.ScoreVersion);
        break;
      case ScanTypes.HRV:
        let hrvScan = await ScanViewUtils.loadHrvScan(scanId, this.api);
        let hrvBreakdown = await ScanViewUtils.getHrvBreakdown(hrvScan);
        await this.setScanScore(ScanTypes.HRV, hrvBreakdown.Score.toNumber(), hrvBreakdown.ScoreVersion);
        break;
      case ScanTypes.ROM:
        let romScan = await ScanViewUtils.loadRomScan(scanId, this.api);
        let romBreakdown = await ScanViewUtils.getRomBreakdown(romScan);
        await this.setScanScore(ScanTypes.ROM, romBreakdown.Score.toNumber(), romBreakdown.ScoreVersion);
        break;
      case ScanTypes.Algo:
        let algoScan = await ScanViewUtils.loadAlgoScan(scanId, this.api);
        let algoBreakdown = await ScanViewUtils.getAlgoBreakdown(algoScan);
        await this.setScanScore(ScanTypes.Algo, algoBreakdown.Score.toNumber(), algoBreakdown.ScoreVersion);
        break;
    }
  }
  public async update(type: ScanTypes, id: string, exam: Exam = this.currentExam)
  {
    switch (type) {
      case ScanTypes.EMG:
        exam.EmgScanId = id;
        break;
      case ScanTypes.Thermal:
        exam.ThermalScanId = id;
        break;
      case ScanTypes.HRV:
        exam.HrvScanId = id;
        break;
      case ScanTypes.ROM:
        exam.RomScanId = id;
        break;
      case ScanTypes.Algo:
        exam.AlgoScanId = id;
        break;
    }

    exam.ReportType = await this.getExamType(exam);

    //check if corescore needed
    if(IsCorescore(exam.ReportType)) {
      let coreScoreBreakdown = ScanViewUtils.calcCoreScore(exam.HrvScore,exam.EmgScore,exam.ThermalScore);

      if(getAge(this._patient.BirthDay) < 13) {
        coreScoreBreakdown = ScanViewUtils.calcPedCoreScore(exam.HrvScore,exam.EmgScore,exam.ThermalScore);
      }
      exam.CoreScore = coreScoreBreakdown.Score;
      exam.CoreScoreVersion = coreScoreBreakdown.Version;
    } else {
      exam.CoreScore = 0;
      exam.CoreScoreVersion = '';
    }

    if(exam.Id == undefined ) {
      // stop saving empty exam
      if (id !== null) {
        let saveInfo = await this.api.addExam(exam).toPromise();
        exam.Id = saveInfo.Id;
      }
    } else if (exam.EmgScanId == null && exam.ThermalScanId == null && exam.HrvScanId == null && exam.RomScanId == null && exam.AlgoScanId == null) {
      // If all are deleted, remove exam too
      await this.api.deleteExam(exam.Id).toPromise();

      exam = null;
      this.clearPatient();
      this.examInProgress = false;
    } else {
      await this.api.modifyExam(exam).toPromise();
    }

  }

  public async recalcCoreScore(exam: Exam, patient: Patient)
  {
    let coreScoreBreakdown = ScanViewUtils.calcCoreScore(exam.HrvScore,exam.EmgScore,exam.ThermalScore);

    if(getAge(patient.BirthDay) < 13) {
      coreScoreBreakdown = ScanViewUtils.calcPedCoreScore(exam.HrvScore,exam.EmgScore,exam.ThermalScore);
    }
    exam.CoreScore = coreScoreBreakdown.Score;
    exam.CoreScoreVersion = coreScoreBreakdown.Version;

    await this.api.modifyExam(exam).toPromise();
    return exam;
  }

  public async clearPatient() {
    await this.setPatient(null);
    this.examInProgress = false;
  }

  public async getExamType( exam: Exam = this.currentExam):Promise<ExamType> {
    if(exam != null) {
      return await this.getExamTypeByExam(exam);
    } else {
      return ExamType.Invalid;
    }
  }

  public async getExamTypeByExam(exam: Exam):Promise<ExamType> {
    let reportType: ExamType;
    let filteredExams : Exam[] = [];

    let allPatientExams = await this.api.getAllExamsForPatientWithId(exam.PatientId).toPromise();
    allPatientExams = allPatientExams.filter(e => new Date(e.CreatedStamp) < new Date(exam.CreatedStamp));
    allPatientExams.sort((a,b) => a.CreatedStamp < b.CreatedStamp ? -1 : a.CreatedStamp > b.CreatedStamp ? 1: 0);
    filteredExams = allPatientExams.filter((e: Exam) => {
      return (e.Id != exam.Id && e.ThermalScanId != null && e.EmgScanId != null && e.HrvScanId != null);
    });
    // save exam
    if(exam.EmgScanId != null && exam.ThermalScanId != null && exam.HrvScanId != null) {
      // is corescore
      if(filteredExams.length < 1) {
        reportType = ExamType.Initial;
      } else if(filteredExams.length < 2) {
        reportType = ExamType.Progress;
      } else if (filteredExams.length < 3) {
        reportType = ExamType.Comparative;
      } else {
        reportType = ExamType.Continuation;
      }
    } else {
      // not corescore
      if((exam.HrvScanId != null && exam.ThermalScanId != null) || (exam.HrvScanId != null && exam.EmgScanId != null)) {
        reportType = ExamType.DualHrv;
      } else if(exam.ThermalScanId != null && exam.EmgScanId != null) {
        reportType = ExamType.DualTherm;
      } else if(exam.ThermalScanId != null || exam.EmgScanId != null || exam.HrvScanId != null) {
        reportType = ExamType.Single;
      } else if(exam.AlgoScanId != null) {
        reportType = ExamType.Algo;
      } else if(exam.RomScanId != null) {
        reportType = ExamType.ROM;
      } else {
        reportType = ExamType.Invalid;
      }
    }
    return reportType;
  }

  async getTodaysScans(pt: Patient) {
    let scans: Scan[] = await this.api.getAllScansForPatientWithId(pt.Id).toPromise();
    let today = new Date();
    today.setHours(0,0,0,0);
    for(let scan of scans) {
      let scanDate = new Date(scan.CreatedStamp);
      if(scanDate.setHours(0,0,0,0) != today.setHours(0,0,0,0)) continue;
      switch (ScanTypes[scan.ScanType+'']) {
        case ScanTypes.HRV:
          if(!this.hrv) {
            this.currentExam.HrvScanId = scan.Id;
          }
          break;
        case ScanTypes.Thermal:
          if(!this.therm) {
            this.currentExam.ThermalScanId = scan.Id;
          }
          break;
        case ScanTypes.EMG:
          if(!this.emg) {
            this.currentExam.EmgScanId = scan.Id;
          }
          break;
        case ScanTypes.Algo:
          if(!this.algo) {
            this.currentExam.AlgoScanId = scan.Id;
          }
          break;
        case ScanTypes.ROM:
          if(!this.rom) {
            this.currentExam.RomScanId = scan.Id;
          }
          break;
      }
    }
  }

  async viewReportByExamProvider(): Promise<{name: string, params: object}> {
    let e: Exam = <Exam>await this.api.getExam(this.examId).toPromise();
    return await this.viewReportByExam(e);
  }

  async viewReportByExam(exam: Exam): Promise<{name: string, params: object}> {
    exam.Patient = await this.api.getPatientById(exam.PatientId).toPromise();
    let exams = await this.api.getAllExamsForPatientWithId(exam.PatientId).toPromise();
    exams.sort((a,b) => a.CreatedStamp > b.CreatedStamp ? -1 : a.CreatedStamp < b.CreatedStamp ? 1: 0);
    exams = exams.filter(e => e.CreatedStamp < exam.CreatedStamp);
    let patAge = getAge(exam.Patient.BirthDay) < 13;
    let reportPrefix = patAge ? 'ReportPed' : 'Report';

    if (exam.EmgScanId && exam.ThermalScanId && exam.HrvScanId) {
      let filteredExams: Exam[] = [];
      // @ts-ignore
      exams = exams.filter(e => ExamType[e.ReportType.toString()] === ExamType.Initial || ExamType[e.ReportType.toString()] === ExamType.Progress || ExamType[e.ReportType.toString()] === ExamType.Comparative || ExamType[e.ReportType.toString()] === ExamType.Continuation);
      switch (ExamType[exam.ReportType.toString()]) {
        case ExamType.Initial:
          return {name: reportPrefix + 'CoreScoreInitialPage', params: {exam: exam}};
        case ExamType.Progress:
          filteredExams = [];
          filteredExams.push(exams[exams.length - 1]);
          return {name: reportPrefix + 'CoreScoreProgressPage', params: {exam: exam, exams: filteredExams}};
        case ExamType.Comparative:
          filteredExams = [];
          filteredExams.push(exams[exams.length - 1], exams[0]);
          return {name: reportPrefix + 'CoreScoreComparativePage', params: {exam: exam, exams: filteredExams}};
        case ExamType.Continuation:
          filteredExams = [];
          filteredExams.push(exams[0], exams[1], exams[2]);
          return {name: reportPrefix + 'CoreScoreContinuationPage', params: {exam: exam, exams: filteredExams}};
      }
    } else if ((exam.HrvScanId && (exam.ThermalScanId || exam.EmgScanId)) || (exam.EmgScanId && (exam.ThermalScanId || exam.HrvScanId)) || (exam.ThermalScanId) && (exam.HrvScanId || exam.EmgScanId)) {
      return {name: 'ReportDualPage', params: {exam: exam}};
    } else if (exam.HrvScanId) {
      return {name: 'ReportHrvPage', params: {scanId: exam.HrvScanId, exam: exam}};
    } else if (exam.ThermalScanId) {
      return {name: 'ReportThermalPage', params: {scanId: exam.ThermalScanId, exam: exam}};
    } else if (exam.EmgScanId) {
      return {name: 'ReportEmgPage', params: {scanId: exam.EmgScanId, exam: exam}};
    }
  }
}

class HistoryItem {
  patient: Patient;
  timestamp: Date;
}

export class ExamDetails {
  examId: string;
  hrvId: string;
  emgId: string;
  thermalId: string;
  romId: string;
  algoId: string;
}



// WEBPACK FOOTER //
// ./src/providers/exam/exam.ts